import { inject, Injectable, signal } from '@angular/core';
import { DestroyableComponent } from '@core/components/destroyable/destroyable.component';
import {
  OperatorTypeEnumRequestDto,
  OrderByDirectionEnum,
  OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto,
  OrderItemFilterRequestDto,
  OrderItemPropertyFilterByEnumRequestDto,
  OrderItemPropertyOrderByEnumRequestDto,
  OrderItemRowTypeEnumRequestDto,
  OrdersLastRequestDto,
  PropertyFilterOfOrderItemPropertyFilterByEnumRequestDtoAndOperatorTypeEnumRequestDto,
} from '@core/services/apiclient';
import { TableFilterResult } from '@shared/models/table-filter.model';
import { LoadOptions } from 'devextreme/data';
import CustomStore from 'devextreme/data/custom_store';
import { firstValueFrom } from 'rxjs';
import { OrderItemsResultInfo } from '../models/dx-tree-list.model';
import { mapToTreeListRow, OrderItemRow, TreeListRowData } from '../models/orders.model';
import { OrdersTreeListUtilsService } from './orders-tree-utils.service';
import { OrdersService } from './orders.service';

export type SelectionOperationType = 'idle' | 'select-all' | 'toggle-order';

@Injectable()
export class OrdersTreeDatasourceService extends DestroyableComponent {
  private readonly ordersService = inject(OrdersService);
  private readonly ordersTreeListUtilsService = inject(OrdersTreeListUtilsService);

  readonly currentData = signal<TreeListRowData[]>([]);
  readonly totalCount = signal<number>(0);
  readonly isPaginatedMode = signal<boolean>(false);
  readonly activeSelectionOperation = signal<SelectionOperationType>('idle');

  private readonly paginationState = signal<{ current: number; size: number }>({ current: 1, size: 25 });
  private readonly orderBy = signal<OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto | null>(null);

  private filters: TableFilterResult[] = [];
  private types: OrderItemRowTypeEnumRequestDto[] = [
    OrderItemRowTypeEnumRequestDto.EventSession,
    OrderItemRowTypeEnumRequestDto.Product,
  ];
  private isDefaultPayload = true;
  private isDefaultOrderBy = true;

  createStore(): CustomStore<TreeListRowData, number> {
    return new CustomStore<TreeListRowData, number>({
      key: 'id',
      load: async (loadOptions: LoadOptions<TreeListRowData>) => this.loadData(loadOptions),
      byKey: async (key: number) => this.getRowById(key),
    });
  }

  setTypesAndFilters(payload: {
    filters: TableFilterResult[];
    types: OrderItemRowTypeEnumRequestDto[];
  }): void {
    this.filters = payload?.filters ?? [];
    this.types = payload?.types?.length
      ? payload.types
      : [OrderItemRowTypeEnumRequestDto.EventSession, OrderItemRowTypeEnumRequestDto.Product];
    this.isDefaultPayload = !this.filters.length;
    this.isDefaultOrderBy = this.isDefaultPayload;
    this.isPaginatedMode.set(this.filters.length > 0);
  }

  setPagination(pagination: { current: number; size: number }): void {
    const current = Math.max(1, pagination?.current ?? 1);
    const size = pagination?.size ?? 25;
    this.paginationState.set({ current, size });
  }

  setOrderBy(orderBy: OrderByPropertyOfOrderItemPropertyOrderByEnumRequestDto | null): void {
    this.orderBy.set(orderBy);
  }

  startSelectionOperation(operation: SelectionOperationType): void {
    this.activeSelectionOperation.set(operation);
  }

  endSelectionOperation(): void {
    this.activeSelectionOperation.set('idle');
  }

  private async loadData(loadOptions: LoadOptions<TreeListRowData>): Promise<TreeListRowData[]> {
    if (this.isInternalSelectionLoad(loadOptions)) {
      return this.loadSelectionRows(loadOptions);
    }

    if (this.isChildrenLoad(loadOptions)) {
      return this.loadChildren(loadOptions.parentIds[0]);
    }

    if (this.isEmptyRootLoadOptions(loadOptions)) {
      return this.currentData();
    }

    return this.loadRootData(loadOptions);
  }

  private loadSelectionRows(loadOptions: LoadOptions<TreeListRowData>): TreeListRowData[] {
    const ids = this.extractIdsFromFilter(loadOptions?.filter);
    if (!ids.length) return [];
    const idSet = new Set(ids);
    return this.currentData().filter((row) => idSet.has(row.id));
  }

  mergeIntoCurrentData(rows: TreeListRowData[]): void {
    const incoming = rows ?? [];
    if (!incoming.length) return;

    this.currentData.set(rows);
  }

  private isEmptyRootLoadOptions(loadOptions: LoadOptions<TreeListRowData>): boolean {
    const options = loadOptions as unknown as {
      parentIds?: unknown[];
      take?: unknown;
      skip?: unknown;
      filter?: unknown;
      sort?: unknown;
      group?: unknown;
    };

    if (Object.keys(loadOptions as object).length === 0) return true;

    const hasParent = Array.isArray(options.parentIds) && options.parentIds.length > 0;
    const hasPaging = typeof options.take === 'number' || typeof options.skip === 'number';
    const hasFilter = options.filter != null;
    const hasSort = Array.isArray(options.sort) ? options.sort.length > 0 : options.sort != null;
    const hasGroup = Array.isArray(options.group) ? options.group.length > 0 : options.group != null;

    return !hasParent && !hasPaging && !hasFilter && !hasSort && !hasGroup;
  }

  private isInternalSelectionLoad(loadOptions: LoadOptions<TreeListRowData>): boolean {
    if (!loadOptions?.filter) return false;
    if (this.hasNonFilterOptions(loadOptions)) return false;
    return this.extractIdsFromFilter(loadOptions.filter).length > 0;
  }

  private hasNonFilterOptions(loadOptions: LoadOptions<TreeListRowData>): boolean {
    const options = loadOptions as unknown as {
      parentIds?: unknown[];
      take?: unknown;
      skip?: unknown;
      sort?: unknown;
      group?: unknown;
    };
    const hasParent = Array.isArray(options.parentIds) && options.parentIds.length > 0;
    const hasPaging = typeof options.take === 'number' || typeof options.skip === 'number';
    const hasSort = Array.isArray(options.sort) ? options.sort.length > 0 : options.sort != null;
    const hasGroup = Array.isArray(options.group) ? options.group.length > 0 : options.group != null;
    return hasParent || hasPaging || hasSort || hasGroup;
  }

  private extractIdsFromFilter(filter: unknown): number[] {
    const ids = new Set<number>();
    this.collectIdsFromFilter(filter, ids);
    return Array.from(ids);
  }

  private collectIdsFromFilter(filter: unknown, ids: Set<number>): void {
    if (!Array.isArray(filter)) return;
    const [field, operator, value] = filter;
    if (field === 'id' && (operator === '=' || operator === '==') && typeof value === 'number') {
      ids.add(value);
    }
    if (field === 'id' && operator === 'in' && Array.isArray(value)) {
      value.filter((id) => typeof id === 'number').forEach((id) => ids.add(id));
    }
    filter.forEach((part) => this.collectIdsFromFilter(part, ids));
  }

  private getRowById(id: number): TreeListRowData | undefined {
    return this.currentData().find((row) => row.id === id);
  }

  buildFilterRequestForOrderNumber(options: {
    orderId: number;
    includes?: OrderItemRowTypeEnumRequestDto[];
  }): OrderItemFilterRequestDto {
    const base = this.getFilters({} as LoadOptions<any>);
    const orderNumberCriteria: PropertyFilterOfOrderItemPropertyFilterByEnumRequestDtoAndOperatorTypeEnumRequestDto =
      {
        property: OrderItemPropertyFilterByEnumRequestDto.OrderNumber,
        condition: OperatorTypeEnumRequestDto.Equal,
        value: `${options.orderId}`,
      };

    return {
      ...base,
      pagination: { current: 0, size: 0, total: 0, enabled: false },
      includes: options.includes ?? base.includes,
      criterias: [...(base.criterias ?? []), orderNumberCriteria],
    };
  }

  buildFilterRequestForSelectAll(options: {
    includes?: OrderItemRowTypeEnumRequestDto[];
    totalCount: number;
  }): OrderItemFilterRequestDto {
    return {
      pagination:
        options.totalCount > 0
          ? { current: 1, size: options.totalCount, total: 0, enabled: true }
          : { current: 0, size: 0, total: 0, enabled: false },
      includes: options.includes ?? this.types,
      criterias: this.ordersTreeListUtilsService.buildCriterias(this.filters),
      orderBy: this.orderBy() ?? {
        direction: OrderByDirectionEnum.Desc,
        property: OrderItemPropertyOrderByEnumRequestDto.OrderNumber,
      },
    };
  }

  private isChildrenLoad(loadOptions: LoadOptions<TreeListRowData>): boolean {
    return loadOptions?.parentIds?.length && loadOptions.parentIds[0] !== null;
  }

  private async loadChildren(subscriptionOrderId: number): Promise<TreeListRowData[]> {
    const parent = this.currentData().find((row) => row?.id === subscriptionOrderId);
    const eventSessionId = this.getActiveEventSessionFilterId();
    const children = await firstValueFrom(this.ordersService.getSubscriptionOrderItems(subscriptionOrderId, eventSessionId));
    const mappedChildren: TreeListRowData[] = (children ?? []).map((row) => {
      const mapped = mapToTreeListRow(row as OrderItemRow);
      return {
        ...mapped,
        customer: mapped.customer ?? parent?.customer,
        parentLabel: parent?.label
      };
    });

    this.currentData.update((existingData) => {
      const childIds = new Set(mappedChildren.map((c) => c.id));
      const filtered = existingData.filter((d) => !childIds.has(d.id));
      return [...filtered, ...mappedChildren];
    });
    return mappedChildren;
  }

  /**
   * Whether the children a group holds in `currentData()` are all of its children. `false` when an
   * event session filter is applied: `loadChildren` then only loads the items of that session, so
   * "every loaded child is selected" no longer means "the whole subscription is selected".
   */
  areLoadedChildrenComplete(): boolean {
    return this.getActiveEventSessionFilterId() == null;
  }

  private getActiveEventSessionFilterId(): number | undefined {
    const eventFilter = this.filters.find(
      (f) => f.field === OrderItemPropertyFilterByEnumRequestDto.EventSessionIdentifier,
    );
    if (eventFilter?.result == null) {
      return undefined;
    }
    const parsed = Number(eventFilter.result);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  private async loadRootData(loadOptions: LoadOptions<TreeListRowData>): Promise<TreeListRowData[]> {
    if (this.filters.length) {
      const response = await firstValueFrom(
        this.ordersService.getFiltredOrders(this.getFilters(loadOptions)),
      );
      const resultInfo: OrderItemsResultInfo = {
        seatCount: response?.includesQuantity ?? 0,
        totalAmount: response?.totalAmount ?? 0,
        ordersCount: response?.ordersQuantity ?? 0,
        totalCount: response?.pagination?.total ?? (response?.list ?? []).length,
      };

      const list = Array.isArray(response?.list) ? response.list : [];
      const mappedAll: TreeListRowData[] = list.map((row) => mapToTreeListRow(row as OrderItemRow));

      this.ordersTreeListUtilsService.setResultInfo(resultInfo);

      this.totalCount.set(resultInfo?.totalCount);
      this.currentData.set(mappedAll);

      return mappedAll;
    }

    const res = await firstValueFrom(
      this.ordersService.getLastOrderItems(this.buildLastOrdersFilter(loadOptions)),
    );
    const list = Array.isArray(res?.list) ? res.list : [];
    const mappedAll: TreeListRowData[] = list.map((row) => mapToTreeListRow(row as OrderItemRow));

    const resultInfo: OrderItemsResultInfo = {
      seatCount: res?.includesQuantity ?? 0,
      totalAmount: res?.totalAmount ?? 0,
      ordersCount: res?.ordersQuantity ?? 0,
      totalCount: mappedAll.length,
    };
    this.ordersTreeListUtilsService.setResultInfo(resultInfo);

    this.totalCount.set(mappedAll.length);
    this.currentData.set(mappedAll);

    return mappedAll;
  }

  private buildLastOrdersFilter(loadOptions?: LoadOptions<TreeListRowData>): OrdersLastRequestDto {
    return {
      includes: this.types,
      orderBy: this.getLastOrdersOrderBy(loadOptions),
    };
  }

  private getLastOrdersOrderBy(loadOptions?: LoadOptions<TreeListRowData>): {
    property: OrderItemPropertyOrderByEnumRequestDto;
    direction: OrderByDirectionEnum;
  } {
    const defaultOrderBy = {
      property: OrderItemPropertyOrderByEnumRequestDto.OrderNumber,
      direction: OrderByDirectionEnum.Desc,
    };

    const sortDescriptor = loadOptions?.sort?.[0] as
      | { selector?: unknown; desc?: unknown }
      | string
      | undefined;
    const selector =
      typeof sortDescriptor === 'string' ? sortDescriptor : (sortDescriptor?.selector as string | undefined);
    const desc = typeof sortDescriptor === 'string' ? false : Boolean(sortDescriptor?.desc);

    if (!selector) return defaultOrderBy;

    const isOrderItemProperty = (Object.values(OrderItemPropertyOrderByEnumRequestDto) as string[]).includes(
      selector,
    );
    if (!isOrderItemProperty) return defaultOrderBy;

    return {
      property: selector as OrderItemPropertyOrderByEnumRequestDto,
      direction: desc ? OrderByDirectionEnum.Desc : OrderByDirectionEnum.Asc,
    };
  }

  protected getFilters(options: LoadOptions<any>): OrderItemFilterRequestDto {
    const orderBy =
      this.orderBy() ??
      (!this.isDefaultPayload
        ? this.ordersTreeListUtilsService.getOrderPropertyBySelector(options?.sort?.[0])
        : null);

    const pagination = this.getRequestPagination();

    return {
      pagination,
      includes: this.types,
      criterias: this.ordersTreeListUtilsService.buildCriterias(this.filters),
      orderBy,
    };
  }

  private getRequestPagination(): {
    current: number;
    size: number;
    total: number;
    enabled: boolean;
  } {
    const { current, size } = this.paginationState();
    if (size === 0) {
      return { current: 0, size: 0, total: 0, enabled: false };
    }

    return { current, size, total: 0, enabled: true };
  }
}
